import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LinkRecovery = ({ open, onClose }) => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/orders/resend`, { email, origin_url: window.location.origin });
      toast.success("If a paid order exists for that email, a fresh download link is on its way.");
      setEmail("");
      onClose();
    } catch {
      toast.error("Could not send right now. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="link-recovery-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm"
          />
          <motion.div
            data-testid="link-recovery-modal"
            role="dialog"
            aria-label="Recover download link"
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[90] w-[calc(100%-2rem)] max-w-md rounded-xl border border-[#D4AF37]/25 bg-[#06333E] p-8 gold-glow"
          >
            <button
              data-testid="link-recovery-close-button"
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#D4AF37]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="w-11 h-11 rounded-full border border-[#D4AF37]/30 flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <h2 className="mt-4 font-display text-xl font-bold text-[#F8FAFC]">Lost your download link?</h2>
            <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">
              Enter the email you bought with and we'll send your personal download link again.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <input
                data-testid="link-recovery-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-[#D4AF37]/20 bg-[#0A404E] px-4 py-3.5 text-[#F8FAFC] placeholder:text-[#94A3B8]/50 outline-none transition-colors focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                autoComplete="email"
              />
              <button
                data-testid="link-recovery-submit-button"
                type="submit"
                disabled={sending}
                className="w-full rounded-full bg-[#D4AF37] py-3.5 font-semibold text-[#052730] transition-all duration-300 hover:bg-[#FCD34D] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#FCD34D] active:scale-[0.98]"
              >
                {sending ? "Sending…" : "Resend my link"}
              </button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LinkRecovery;
